import { Link, Link as RouterLink } from "react-router-dom";
import { useTheme } from "next-themes";
import { useEffect, useState, useCallback } from "react";
import { Gamepad2, LogOut, Menu, Settings, User, Plane, ShoppingBag } from "lucide-react";
import { useCart } from "@/lib/cart";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import type { Profile } from "@/types/profile";
import { useMounted } from "./useMounted";

/**
 * VOY-477 PR #280 fix: avatar is now a clickable DropdownMenuTrigger
 * that restores the profile / trips / settings / sign-out menu the
 * user had on `/search-plus` via the global `AppHeader` + `UserNav`.
 * Before this change, the avatar was a non-clickable `<div>` — fine on
 * State A landing but broken in State B (the user lost the only way
 * to reach their account from the unified `/` surface).
 *
 * The dropdown items mirror `UserNav` exactly (profile / trips /
 * settings / admin / sign-in/out). The TopNav's cream `.nav-avatar`
 * styling is preserved by keeping it as the visible trigger; the
 * dropdown opens below-right.
 */
export function TopNav({ userInitial }: { userInitial?: string }) {
  const { setTheme, resolvedTheme } = useTheme();
  const mounted = useMounted();
  const isDark = mounted ? resolvedTheme === "dark" : false;
  const { user, signOut } = useAuth();
  const isAuthenticated = !!user;

  // VOY-494: cart trigger lives next to the avatar. Visible to
  // authenticated AND anonymous users — the cart context is global and
  // persists across sign-in via Supabase merge on auth state change.
  const { cart, openCart } = useCart();
  const itemCount = cart.itemCount;
  const cartLabel = itemCount > 0
    ? `View cart, ${itemCount} item${itemCount === 1 ? '' : 's'}`
    : 'View cart, empty';

  const [profile, setProfile] = useState<Profile | null>(null);
  const loadProfile = useCallback(async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();
    if (error) {
      if (error.code !== "PGRST116") {
        console.error("Error loading profile:", error);
      }
      return;
    }
    setProfile(data);
  }, [user]);
  useEffect(() => {
    if (user) loadProfile();
  }, [user, loadProfile]);

  return (
    <nav className="topnav">
      <Link to="/" className="brand">
        V<em>oy</em>
      </Link>
      <div className="nav-links">
        <Link to="/you">You</Link>
        <Link to="/">Search+</Link>
        <Link to="/companion">Companion</Link>
        <Link to="/games">Gaming</Link>
        <Link to="/subscription">Subscription</Link>
      </div>
      <div className="nav-right">
        {/*
          VOY-506: mobile hamburger menu. The old Header had a separate
          MainNav component that surfaced the 5 nav items as a dropdown
          on narrow viewports. The v6 .nav-links wrapper hides at
          max-width: 640px, leaving no menu on mobile — restore parity
          by exposing the same 5 links inside a dropdown. CSS rules in
          voy-v6.css (.nav-mobile) control desktop hide / mobile show.
        */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="nav-mobile"
              aria-label="Open navigation menu"
              title="Menu"
            >
              <Menu width={18} height={18} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          {/*
            Bot review: no `forceMount` on the mobile DropdownMenuContent.
            Letting Radix unmount on close keeps the 5 nav links out of
            the a11y tree when the menu is collapsed — otherwise screen
            readers would enumerate hidden links on every page load.
            The account dropdown above keeps `forceMount` because it
            renders the menu trigger conditionally on auth state and the
            consumer relies on the open state being controlled.
          */}
          <DropdownMenuContent
            className="w-44 z-[100] bg-white dark:bg-gray-900 text-black dark:text-white rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
            align="end"
          >
            <DropdownMenuItem asChild>
              <RouterLink to="/you">You</RouterLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <RouterLink to="/">Search+</RouterLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <RouterLink to="/companion">Companion</RouterLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <RouterLink to="/games">Gaming</RouterLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <RouterLink to="/subscription">Subscription</RouterLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* VOY-494: cart trigger. Visible in both full and compact
            modes because TopNav renders in both. Badge appears only
            when items are present. */}
        <button
          type="button"
          className="cart-trigger"
          onClick={openCart}
          aria-label={cartLabel}
        >
          <ShoppingBag />
          {itemCount > 0 && (
            <span className="cart-badge" aria-hidden="true">
              {itemCount > 99 ? '99+' : itemCount}
            </span>
          )}
        </button>
        <button
          type="button"
          className="theme-toggle"
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          onClick={() => setTheme(isDark ? "light" : "dark")}
        >
          <svg className="ico-sun" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4.5" />
            <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5 5l1.4 1.4M17.6 17.6L19 19M5 19l1.4-1.4M17.6 6.4L19 5" />
          </svg>
          <svg className="ico-moon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.5 14.5A8.5 8.5 0 1 1 9.5 3.5a7 7 0 0 0 11 11Z" />
          </svg>
        </button>
        {userInitial ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="nav-avatar"
                aria-label="Account menu"
                title="Account"
              >
                {userInitial}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56 z-[100] bg-white dark:bg-gray-900 text-black dark:text-white rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
              align="end"
              forceMount
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {profile?.username || "Account"}
                    {profile?.role === "admin" && (
                      <span className="ml-2 text-xs bg-primary text-primary-foreground px-1 rounded">
                        Admin
                      </span>
                    )}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {isAuthenticated ? user?.email : "Sign in to access your profile"}
                  </p>
                </div>
              </DropdownMenuLabel>
              {isAuthenticated && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <RouterLink to="/profile">
                        <User className="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </RouterLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <RouterLink to="/trips">
                        <Plane className="mr-2 h-4 w-4" />
                        <span>My Trips</span>
                      </RouterLink>
                    </DropdownMenuItem>
                    {/* PR #280 review note: mirrors the pre-existing
                        UserNav item (also no onClick / no route target).
                        Wiring this to a real settings panel is a separate
                        ticket — leaving inert to preserve visual parity
                        with the legacy menu. */}
                    <DropdownMenuItem>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </>
              )}
              {profile?.role === "admin" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <RouterLink to="/admin/site">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Manage Site</span>
                      </RouterLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <RouterLink to="/admin/games">
                        <Gamepad2 className="mr-2 h-4 w-4" />
                        <span>Manage Games</span>
                      </RouterLink>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </>
              )}
              <DropdownMenuSeparator />
              {isAuthenticated ? (
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem asChild>
                  <RouterLink to="/auth/sign-in">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign in</span>
                  </RouterLink>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link to="/auth/sign-in" className="nav-ghost">
            Sign in
          </Link>
        )}
      </div>
    </nav>
  );
}
